/**
 * Utilities for managing organization-specific favicons and app icons
 */

export interface FaviconConfig {
  organizationName: string;
  organizationSlug?: string;
  organizationLogoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

/**
 * Reads `rel` from a link-like DOM node. Browser extensions (e.g. Obscura)
 * can inject icon links whose `rel` is missing or non-string.
 */
export function getLinkRel(link: { rel?: unknown; getAttribute?: (name: string) => string | null } | null | undefined): string {
  if (!link) return '';
  if (typeof link.rel === 'string') return link.rel;
  try {
    const attr = link.getAttribute?.('rel');
    return typeof attr === 'string' ? attr : '';
  } catch {
    return '';
  }
}

/** True when a link should be treated as an apple-touch-icon (leave it alone). */
export function isAppleIconRel(rel: unknown): boolean {
  return typeof rel === 'string' && rel.toLowerCase().includes('apple');
}

/**
 * Updates the page favicon dynamically based on organization
 * This updates ALL icon-related link elements to ensure consistency
 * Uses remove/recreate approach to force Safari to reload the favicon
 */
export function updateFavicon(logoUrl: string) {
  if (typeof document === 'undefined' || !logoUrl) {
    return;
  }

  try {
    console.log('[Favicon] Updating favicon to:', logoUrl);

    // Find all icon-related link elements (excluding apple-touch-icon)
    const iconLinks = document.querySelectorAll("link[rel*='icon']");
    console.log('[Favicon] Found existing icon links:', iconLinks.length);

    // Remove all existing icon links (except apple-touch-icon).
    // Guard `rel` — extensions can inject <link> nodes with undefined rel.
    iconLinks.forEach(node => {
      const rel = getLinkRel(node as HTMLLinkElement);
      if (!isAppleIconRel(rel)) {
        console.log('[Favicon] Removing link:', rel, (node as HTMLLinkElement).href);
        node.remove();
      }
    });

    const head = document.head;
    if (!head) {
      console.warn('[Favicon] document.head is missing; skipping favicon update');
      return;
    }

    // Force a DOM reflow to ensure Safari processes the removal
    void head.offsetHeight;

    // Create new favicon links with cache-busting timestamp
    const cacheBuster = `t=${Date.now()}`;
    const newHref = `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}${cacheBuster}`;
    const iconType = logoUrl.endsWith('.svg') ? 'image/svg+xml' : 'image/png';

    // Add 'icon' link with sizes attribute for Safari
    const iconLink = document.createElement('link');
    iconLink.rel = 'icon';
    iconLink.type = iconType;
    iconLink.href = newHref;
    // Safari pays more attention when sizes is specified
    if (!logoUrl.endsWith('.svg')) {
      iconLink.setAttribute('sizes', '32x32');
    }
    head.appendChild(iconLink);

    // Add 'shortcut icon' for older browsers
    const shortcutLink = document.createElement('link');
    shortcutLink.rel = 'shortcut icon';
    shortcutLink.type = iconType;
    shortcutLink.href = newHref;
    head.appendChild(shortcutLink);

    console.log('[Favicon] Added new icon links:', newHref);
  } catch (error) {
    console.warn('[Favicon] Failed to update favicon:', error);
  }
}

/**
 * Updates the Apple Touch Icon for iOS home screen
 * Uses remove/recreate approach to force Safari to reload the icon
 */
export function updateAppleTouchIcon(logoUrl: string) {
  if (typeof document === 'undefined' || !logoUrl) {
    return;
  }

  try {
    // Remove existing apple-touch-icon
    const existingLink = document.querySelector("link[rel='apple-touch-icon']");
    existingLink?.remove();

    const head = document.head;
    if (!head) return;

    // Create new apple-touch-icon with cache-busting timestamp
    const newLink = document.createElement('link');
    newLink.rel = 'apple-touch-icon';
    newLink.href = `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    head.appendChild(newLink);
  } catch (error) {
    console.warn('[Favicon] Failed to update apple-touch-icon:', error);
  }
}

/**
 * Updates the app title for iOS home screen
 */
export function updateAppleWebAppTitle(title: string) {
  if (typeof document === 'undefined') {
    return;
  }

  try {
    let meta = document.querySelector("meta[name='apple-mobile-web-app-title']") as HTMLMetaElement | null;
    if (meta) {
      meta.content = title;
    } else {
      const head = document.head;
      if (!head) return;
      meta = document.createElement('meta');
      meta.name = 'apple-mobile-web-app-title';
      meta.content = title;
      head.appendChild(meta);
    }
  } catch (error) {
    console.warn('[Favicon] Failed to update apple-mobile-web-app-title:', error);
  }
}

/**
 * Updates the manifest link to point to organization-specific manifest
 */
export function updateManifestLink(manifestUrl: string) {
  if (typeof document === 'undefined' || !manifestUrl) {
    return;
  }

  try {
    // Remove existing manifest links to prevent conflicts
    const existingLinks = document.querySelectorAll("link[rel='manifest']");
    existingLinks.forEach(link => link.remove());

    const head = document.head;
    if (!head) return;

    // Create new manifest link with cache-busting
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = `${manifestUrl}?t=${Date.now()}`;
    head.appendChild(link);
  } catch (error) {
    console.warn('[Favicon] Failed to update manifest link:', error);
  }
}

/**
 * Generates a data URL for a simple icon with organization initials
 * This is used as a fallback when no logo is available
 */
export function generateInitialsIcon(
  initials: string,
  backgroundColor: string = '#4A1D96',
  textColor: string = '#FFFFFF',
  size: number = 512
): string {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return '';
  }

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, size, size);

  ctx.fillStyle = textColor;
  ctx.font = `bold ${size * 0.4}px -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initials.toUpperCase().slice(0, 2), size / 2, size / 2);

  return canvas.toDataURL('image/png');
}

/**
 * Generates a composite icon with a colored background and logo overlay
 * Returns a Promise that resolves to a data URL
 */
export function generateCompositeIcon(
  logoUrl: string,
  backgroundColor: string = '#4A1D96',
  size: number = 512,
  organizationName?: string
): Promise<string> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('[generateCompositeIcon] Could not get canvas context');
      // Fall back to initials if we have the name
      if (organizationName) {
        const initials = getOrganizationInitials(organizationName);
        resolve(generateInitialsIcon(initials, backgroundColor, '#FFFFFF', size));
      } else {
        resolve('');
      }
      return;
    }

    // Draw background with rounded corners
    const radius = size * 0.15;
    ctx.fillStyle = backgroundColor;
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(size - radius, 0);
    ctx.quadraticCurveTo(size, 0, size, radius);
    ctx.lineTo(size, size - radius);
    ctx.quadraticCurveTo(size, size, size - radius, size);
    ctx.lineTo(radius, size);
    ctx.quadraticCurveTo(0, size, 0, size - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.fill();

    // Load and draw the logo
    const img = new Image();
    
    // Set crossOrigin for CORS-enabled sources to allow canvas export
    // Supabase public storage buckets DO send CORS headers
    const isSameOrigin = logoUrl.startsWith(window.location.origin) || logoUrl.startsWith('data:');
    const isSupabaseStorage = logoUrl.includes('supabase.co/storage');
    
    // Always set crossOrigin for Supabase storage and same-origin URLs
    if (isSameOrigin || isSupabaseStorage) {
      img.crossOrigin = 'anonymous';
    }
    
    // Set a timeout for image loading
    const timeout = setTimeout(() => {
      console.warn('[generateCompositeIcon] Image load timeout, using initials fallback');
      if (organizationName) {
        const initials = getOrganizationInitials(organizationName);
        // Draw initials on top of existing background
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `bold ${size * 0.4}px -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initials.toUpperCase().slice(0, 2), size / 2, size / 2);
      }
      resolve(canvas.toDataURL('image/png'));
    }, 5000);
    
    img.onload = () => {
      clearTimeout(timeout);
      // Calculate logo size (70% of icon size) and center it
      const logoSize = size * 0.7;
      const logoX = (size - logoSize) / 2;
      const logoY = (size - logoSize) / 2;
      
      try {
        ctx.drawImage(img, logoX, logoY, logoSize, logoSize);
        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        console.error('[generateCompositeIcon] Error drawing image (likely CORS):', error);
        // If we can't draw due to tainted canvas, use initials
        if (organizationName) {
          const initials = getOrganizationInitials(organizationName);
          resolve(generateInitialsIcon(initials, backgroundColor, '#FFFFFF', size));
        } else {
          resolve(canvas.toDataURL('image/png'));
        }
      }
    };

    img.onerror = (error) => {
      clearTimeout(timeout);
      console.warn('[generateCompositeIcon] Failed to load logo:', error);
      // If logo fails to load, add initials on the background
      if (organizationName) {
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `bold ${size * 0.4}px -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initials = getOrganizationInitials(organizationName);
        ctx.fillText(initials.toUpperCase().slice(0, 2), size / 2, size / 2);
      }
      resolve(canvas.toDataURL('image/png'));
    };

    console.log('[generateCompositeIcon] Loading logo:', logoUrl, 'crossOrigin:', img.crossOrigin);
    img.src = logoUrl;
  });
}

/**
 * Gets the initials from an organization name
 */
export function getOrganizationInitials(name: string): string {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0] || ''}${words[1][0] || ''}`;
  }
  return (name || '').slice(0, 2);
}

/**
 * Generates a manifest.json content for an organization
 * Uses a callback to provide the icon data URL once generated
 */
export function generateManifest(config: FaviconConfig, iconDataUrl?: string): string {
  const { organizationName, organizationSlug, organizationLogoUrl, primaryColor, secondaryColor } = config;

  // Use absolute URLs for blob-based manifests
  const baseUrl = window.location.origin;
  // Use provided data URL, or fall back to organization logo, or default icon
  const iconUrl = iconDataUrl || organizationLogoUrl || `${baseUrl}/icon.svg`;
  // Add trailing slash to start_url to match scope
  const startUrl = organizationSlug ? `${baseUrl}/${organizationSlug}/` : `${baseUrl}/`;
  const scopeUrl = organizationSlug ? `${baseUrl}/${organizationSlug}/` : `${baseUrl}/`;

  const manifest = {
    name: organizationName,
    short_name: organizationName.length > 12 ? organizationName.slice(0, 12) : organizationName,
    description: `Football competitions and predictions for ${organizationName}`,
    start_url: startUrl,
    scope: scopeUrl,
    display: 'standalone',
    theme_color: primaryColor || '#4A1D96',
    background_color: secondaryColor || '#5B2BA6',
    orientation: 'portrait',
    icons: [
      {
        src: iconUrl,
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any maskable'
      },
      {
        src: iconUrl,
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any maskable'
      }
    ]
  };

  return JSON.stringify(manifest, null, 2);
}

/**
 * Creates and downloads a manifest.json file dynamically
 */
export function createDynamicManifest(config: FaviconConfig, iconDataUrl?: string): string {
  const manifestContent = generateManifest(config, iconDataUrl);
  const blob = new Blob([manifestContent], { type: 'application/json' });
  return URL.createObjectURL(blob);
}

/**
 * Gets the Edge Function URL for icon generation
 * @param composite - If true, generates a composite icon with logo on colored background
 */
export function getEdgeFunctionIconUrl(config: FaviconConfig, size: number = 512, composite: boolean = false): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://ibztzxgdbluqzisqkiry.supabase.co';
  const params = new URLSearchParams({
    name: config.organizationName,
    size: size.toString(),
  });

  if (config.primaryColor) {
    params.set('primaryColor', config.primaryColor);
  }
  if (config.secondaryColor) {
    params.set('secondaryColor', config.secondaryColor);
  }
  if (config.organizationLogoUrl) {
    params.set('logoUrl', config.organizationLogoUrl);
  }
  if (composite) {
    params.set('composite', 'true');
  }

  return `${supabaseUrl}/functions/v1/generate-org-icon?${params.toString()}`;
}

/**
 * Updates all organization-specific branding elements
 * Uses the edge function to generate composite icons server-side (avoids CORS issues)
 */
export async function updateOrganizationBranding(config: FaviconConfig) {
  const { organizationName, organizationLogoUrl, primaryColor } = config;

  console.log('[updateOrganizationBranding] Called with config:', {
    organizationName,
    organizationLogoUrl,
    primaryColor,
    hasLogoUrl: !!organizationLogoUrl
  });

  // For favicon (browser tab), use the logo directly if available
  if (organizationLogoUrl) {
    updateFavicon(organizationLogoUrl);
  } else {
    const initials = getOrganizationInitials(organizationName);
    const initialsIcon = generateInitialsIcon(initials, primaryColor || '#4A1D96', '#FFFFFF', 512);
    updateFavicon(initialsIcon);
  }

  // For PWA icons (apple-touch-icon and manifest), use the edge function
  // This generates a composite icon server-side with logo on colored background
  const edgeFunctionIconUrl = getEdgeFunctionIconUrl(config, 512, !!organizationLogoUrl);
  console.log('[updateOrganizationBranding] Using edge function URL for PWA icons:', edgeFunctionIconUrl);

  // Update apple-touch-icon to use edge function
  updateAppleTouchIcon(edgeFunctionIconUrl);

  // Note: apple-mobile-web-app-title is now handled by useMetaTags hook
  // to ensure it matches the full page title (org name + description)

  // Create manifest with edge function URL for icons
  const manifestUrl = createDynamicManifest(config, edgeFunctionIconUrl);
  updateManifestLink(manifestUrl);
}

/**
 * Restores default Footy Picks branding
 * Used when navigating back to homepage or clearing organization context
 */
export function restoreDefaultBranding() {
  try {
    const defaultIconUrl = '/icon.svg';
    updateFavicon(defaultIconUrl);
    updateAppleTouchIcon(defaultIconUrl);
    updateAppleWebAppTitle('Footy Picks');

    const manifestUrl = createDynamicManifest({
      organizationName: 'Footy Picks',
      primaryColor: '#4A1D96',
      secondaryColor: '#5B2BA6'
    });
    updateManifestLink(manifestUrl);
  } catch (error) {
    console.warn('[Favicon] Failed to restore default branding:', error);
  }
}
